// Custom layout and card extensions for the catalog entity overview tab.
//
// Architecture:
//   - EntityContentLayoutBlueprint attaches to entity-content:catalog/overview → layouts.
//     This layout intentionally has no entity filter, so it replaces the default
//     overview layout for all catalog entity kinds.
//   - EntityCardBlueprint extensions attach to entity-content:catalog/overview → cards.
//     Generic cards have no entity filter and render for every entity. Operational
//     cards keep a component filter because they are Kubernetes/service placeholders.
//   - Built-in catalog overview cards are disabled in app-config*.yaml so they do
//     not render alongside the redesigned card set.
//
// Card ordering within each type is determined by registration order in
// catalogModule.tsx.

import {
  EntityContentLayoutBlueprint,
  EntityCardBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import type { EntityContentLayoutProps } from '@backstage/plugin-catalog-react/alpha';
import { Grid } from '@material-ui/core';

// ── Layout ──────────────────────────────────────────────────

/**
 * Layout component for catalog entities. Receives all matching cards via props
 * and renders them in a two-column grid: main column (content cards) + right
 * rail (info cards).
 */
function ComponentOverviewLayout({ cards }: EntityContentLayoutProps) {
  const contentCards = cards.filter(c => !c.type || c.type === 'content');
  const infoCards = cards.filter(c => c.type === 'info');

  return (
    <Grid container spacing={2}>
      {/* ── Main column ─────────────────────────────────── */}
      <Grid item xs={12} md={9}>
        <Grid container spacing={2}>
          {contentCards.map((card, i) => (
            <Grid item xs={12} key={i}>
              {card.element}
            </Grid>
          ))}
        </Grid>
      </Grid>

      {/* ── Right rail ──────────────────────────────────── */}
      <Grid item xs={12} md={3}>
        <Grid container spacing={2}>
          {infoCards.map((card, i) => (
            <Grid item xs={12} key={i}>
              {card.element}
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
}

export const componentOverviewLayout = EntityContentLayoutBlueprint.make({
  name: 'overview-layout',
  params: {
    loader: async () => ComponentOverviewLayout,
  },
});

// ── Cards ──────────────────────────────────────────────────

export const aboutCard = EntityCardBlueprint.make({
  name: 'overview-about',
  params: {
    loader: async () => {
      const { AboutCard } = await import('./tiles/AboutCard');
      return <AboutCard />;
    },
  },
});

export const serviceStatusCard = EntityCardBlueprint.make({
  name: 'component-service-status',
  params: {
    filter: { kind: 'component' },
    loader: async () => {
      const { ServiceStatusCard } = await import('./tiles/ServiceStatusCard');
      return <ServiceStatusCard />;
    },
  },
});

export const apisCard = EntityCardBlueprint.make({
  name: 'overview-apis',
  params: {
    loader: async () => {
      const { ApisCard } = await import('./tiles/ApisCard');
      return <ApisCard />;
    },
  },
});

export const dependenciesCard = EntityCardBlueprint.make({
  name: 'overview-dependencies',
  params: {
    type: 'info',
    loader: async () => {
      const { DependenciesCard } = await import('./tiles/DependenciesCard');
      return <DependenciesCard />;
    },
  },
});

// ── Right rail cards (type: info unless overridden) ─────────

export const metadataCard = EntityCardBlueprint.make({
  name: 'overview-metadata',
  params: {
    type: 'info',
    loader: async () => {
      const { MetadataCard } = await import('./tiles/MetadataCard');
      return <MetadataCard />;
    },
  },
});

export const linksCard = EntityCardBlueprint.make({
  name: 'overview-links',
  params: {
    type: 'info',
    loader: async () => {
      const { LinksCard } = await import('./tiles/LinksCard');
      return <LinksCard />;
    },
  },
});

export const relationsCard = EntityCardBlueprint.make({
  name: 'overview-relations',
  params: {
    loader: async () => {
      const { RelationsCard } = await import('./tiles/RelationsCard');
      return <RelationsCard />;
    },
  },
});

export const tagsCard = EntityCardBlueprint.make({
  name: 'overview-tags',
  params: {
    type: 'info',
    loader: async () => {
      const { TagsCard } = await import('./tiles/TagsCard');
      return <TagsCard />;
    },
  },
});
